import React, { useState } from 'react';
import { Modal } from '@mui/material';
import moment from 'moment';
import { X, FileText, CheckCircle, Eye } from 'phosphor-react';
import { twMerge } from 'tailwind-merge';

import { AppFile, DillDispute, DillTransaction } from '@dill/dill-shared';
import { AppButton } from '../../../general/AppButton/AppButton';
import { AppPaidInvoicesModal } from '../../../general/AppPaidInvoicesModal/AppPaidInvoicesModal';
import { useAppDispatch } from '../../../../redux/store';
import { getTransactionById } from '../../../../redux/services/paymentsService';

interface DisputeDetailsModalProps {
  open: boolean;
  dispute: DillDispute | null;
  onClose: () => void;
  onMarkResolved: (disputeId: string) => void;
}

export const DisputeDetailsModal = ({
  open,
  dispute,
  onClose,
  onMarkResolved,
}: DisputeDetailsModalProps) => {
  const dispatch = useAppDispatch();
  const [selectedTransaction, setSelectedTransaction] = useState<DillTransaction | null>(null);
  const [openPaidInvoicesModal, setOpenPaidInvoicesModal] = useState(false);
  const [isLoadingTransaction, setIsLoadingTransaction] = useState(false);

  if (!dispute) return null;

  const handleViewPayment = async () => {
    if (!dispute.transactionId) return;

    setIsLoadingTransaction(true);
    try {
      const resp = await dispatch(getTransactionById({ transactionId: dispute.transactionId }));
      if (resp.type === 'payments/getTransactionById/fulfilled' && resp.payload) {
        const payload: any = resp.payload;
        if (payload.data) {
          setSelectedTransaction(payload.data);
          setOpenPaidInvoicesModal(true);
        }
      }
    } catch (error) {
      console.error('Error fetching transaction:', error);
    } finally {
      setIsLoadingTransaction(false);
    }
  };

  const handleClosePaidInvoicesModal = () => {
    setOpenPaidInvoicesModal(false);
    setSelectedTransaction(null);
  };

  return (
    <>
      <AppPaidInvoicesModal
        open={openPaidInvoicesModal}
        handleClose={handleClosePaidInvoicesModal}
        accountType="supplier"
        selectedTransaction={selectedTransaction}
        onViewMainPayment={() => {}}
      />
      <Modal open={open} onClose={onClose} aria-labelledby="dispute-details-modal">
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 bg-white rounded-lg shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
          <div className="flex items-center justify-between p-4 border-b">
            <h2 className="text-lg font-semibold">Dispute Details</h2>
            <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
              <X size={24} />
            </button>
          </div>

          <div className="p-4 space-y-4">
            {/* Status */}
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-500">Status</span>
              <span
                className={`px-2 py-1 rounded-full text-xs font-medium ${
                  dispute.isResolved
                    ? 'bg-green-100 text-green-800'
                    : 'bg-yellow-100 text-yellow-800'
                }`}>
                {dispute.isResolved ? 'Resolved' : 'Pending'}
              </span>
            </div>

            {/* Dispute Type */}
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-500">Type</span>
              <span
                className={twMerge(
                  'px-2 py-1 rounded-full text-xs font-medium',
                  dispute.disputeType === 'PAYMENT'
                    ? 'bg-blue-100 text-blue-800'
                    : dispute.disputeType === 'INVOICE'
                    ? 'bg-purple-100 text-purple-800'
                    : 'bg-gray-100 text-gray-800'
                )}>
                {dispute.disputeType || 'OTHER'}
              </span>
            </div>

            {/* Created Date */}
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-500">Created</span>
              <span className="text-sm">
                {dispute.createdAt ? moment(dispute.createdAt).format('MM/DD/YYYY') : '-'}
              </span>
            </div>

            {/* Linked Payment */}
            {dispute.transactionId && (
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-500">Linked Payment</span>
                <AppButton
                  text={isLoadingTransaction ? 'Loading...' : 'View Payment'}
                  type="PRIMARY"
                  icon={<Eye size={14} className="mr-1" />}
                  buttonStyles={{ padding: '4px 8px', fontSize: '12px' }}
                  onClick={handleViewPayment}
                  isDisabled={isLoadingTransaction}
                />
              </div>
            )}

            {/* Reason */}
            <div>
              <span className="text-sm text-gray-500 block mb-1">Reason</span>
              <p className="text-sm bg-gray-50 p-3 rounded-md">
                {dispute.disputeReason || 'No reason provided'}
              </p>
            </div>

            {/* Notes */}
            <div>
              <span className="text-sm text-gray-500 block mb-1">Notes</span>
              <p className="text-sm bg-gray-50 p-3 rounded-md">
                {dispute.disputeNotes || 'No notes provided'}
              </p>
            </div>

            {/* Attachments */}
            <div>
              <span className="text-sm text-gray-500 block mb-2">Attachments</span>
              {dispute.disputeAttachments && dispute.disputeAttachments.length > 0 ? (
                <div className="space-y-2">
                  {dispute.disputeAttachments.map((file: AppFile, idx: number) => (
                    <a
                      key={idx}
                      href={file.url || '#'}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center p-2 bg-gray-50 rounded-md hover:bg-gray-100 transition-colors">
                      <FileText size={18} className="text-blue-500 mr-2" />
                      <span className="text-sm text-blue-600 hover:underline">
                        {file.name || `Attachment ${idx + 1}`}
                      </span>
                    </a>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-gray-400">No attachments</p>
              )}
            </div>
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-2 p-4 border-t">
            <AppButton text="Close" type="SECONDARY" onClick={onClose} />
            {!dispute.isResolved && (
              <AppButton
                text="Mark as Resolved"
                type="PRIMARY"
                icon={<CheckCircle size={16} className="mr-1" />}
                onClick={() => {
                  onMarkResolved(dispute.id);
                  onClose();
                }}
              />
            )}
          </div>
        </div>
      </Modal>
    </>
  );
};
